import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import type { OverrideKind } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  useAcceptAiScriptCellOverride,
  useDismissAiScriptCellOverride,
} from "@/payroll/ai-script/query";
import {
  AI_SPREADSHEET_OVERRIDE_REVIEW_REASON,
  type IAiSpreadsheetCellOverride,
} from "@/payroll/ai-script/types";
import { InlineConfirmation } from "./inline-confirmation";
import { showPersistentEditMutationError } from "./shared";

interface OverrideRowActionsProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  kind: OverrideKind;
  canResolve: boolean | null;
  isListCard: boolean;
  onFocus?: () => void;
  onResolved?: () => void;
  onWorkbookMutated?: () => void;
}

export const OverrideRowActions = ({
  override,
  businessId,
  runId,
  kind,
  canResolve,
  isListCard,
  onFocus,
  onResolved,
  onWorkbookMutated,
}: OverrideRowActionsProps) => {
  const accept = useAcceptAiScriptCellOverride(businessId);
  const dismiss = useDismissAiScriptCellOverride(businessId);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isAmbiguous =
    override.review_reason === AI_SPREADSHEET_OVERRIDE_REVIEW_REASON.ambiguous;
  const busy = accept.isPending || dismiss.isPending;
  const showActions = canResolve && !(isListCard && kind === "applied");

  if (!showActions) return null;

  const handleMutationError = (message: string, error: unknown) => {
    if (showPersistentEditMutationError(message, error)) onResolved?.();
  };

  const confirmDiscard = () =>
    dismiss.mutate(
      { runId, overrideId: override.id },
      {
        onSuccess: () => {
          setConfirmDiscardOpen(false);
          onWorkbookMutated?.();
          onResolved?.();
        },
        onError: (error) => {
          setConfirmDiscardOpen(false);
          handleMutationError("Couldn't discard this edit", error);
        },
      },
    );

  const stopCardClick = (event: React.MouseEvent) => event.stopPropagation();

  if (confirmDiscardOpen) {
    return (
      <InlineConfirmation
        message="Discard this edit? The cell goes back to the value the payroll script produced."
        confirmLabel="Discard edit"
        isPending={dismiss.isPending}
        onCancel={() => setConfirmDiscardOpen(false)}
        onConfirm={confirmDiscard}
      />
    );
  }

  return (
    <Stack
      direction="row"
      gap={2}
      align="center"
      justify="end"
      className="w-full flex-wrap pt-1"
      onClick={stopCardClick}
    >
      <Button
        variant="outline"
        size="xs"
        onClick={() => setConfirmDiscardOpen(true)}
        disabled={dismiss.isPending || busy}
        aria-busy={dismiss.isPending || undefined}
      >
        {dismiss.isPending && <Spinner aria-hidden data-icon="inline-start" />}
        Discard
      </Button>
      {isAmbiguous ? (
        onFocus ? (
          <Button variant="secondary" size="xs" onClick={onFocus}>
            Pick row
          </Button>
        ) : null
      ) : kind === "needs_review" ? (
        <Button
          variant="secondary"
          size="xs"
          onClick={() =>
            accept.mutate(
              { runId, overrideId: override.id },
              {
                onSuccess: () => {
                  onWorkbookMutated?.();
                  onResolved?.();
                },
                onError: (error) =>
                  handleMutationError("Couldn't keep this edit", error),
              },
            )
          }
          disabled={accept.isPending || busy}
          aria-busy={accept.isPending || undefined}
        >
          {accept.isPending && <Spinner aria-hidden data-icon="inline-start" />}
          Keep edit
        </Button>
      ) : null}
    </Stack>
  );
};
